import { useEffect, useState } from 'react';
import { Button, Modal, Divider, Form, Select, message } from 'antd';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { updateCurrencySwapPairs } from '../../methods/redux/reducers/currency';

interface IEditCurrency {
	currency: any;
	setRefresh: (e: any) => void;
}
type Pair = {
	id: string;
	symbol: string;
};

export const StyledFormItem = styled(Form.Item)`
	.ant-row {
		display: block;
	}
`;

const EditCurrencySwap = ({ currency, setRefresh }: IEditCurrency) => {
	const [form] = Form.useForm();
	const dispatch = useAppDispatch();
	const [allowedPairs, setAllowedPairs] = useState<Pair[]>([]);
	const { currencies } = useAppSelector((store: any) => store.currencies);

	const [visible, setVisible] = useState(false);
	const [isLoadingPair, setIsLoadingPair] = useState(false);

	useEffect(() => {
		setAllowedPairs(currency?.allowedSwapPairs);
	}, [currency]);

	const currencySelect = currencies.map((item: any) => ({
		label: item.symbol,
		value: item._id,
	}));

	const handleAddPair = (selectedId: any) => {
		const selectedPair = currencySelect.find(
			(p: any) => p.value === selectedId
		);
		if (!selectedPair) return;

		const pair = { id: selectedPair.value, symbol: selectedPair.label };

		const alreadyExists = allowedPairs.some((p) => p.id === pair.id);
		if (alreadyExists) return;

		setAllowedPairs([...allowedPairs, pair]);
	};

	const handleRemovePair = (idToRemove: string) => {
		setAllowedPairs(allowedPairs.filter((pair) => pair.id !== idToRemove));
	};

	const handleSwapPairsUpdate = async () => {
		try {
			setIsLoadingPair(true);

			const jobData = {
				currencyId: currency?._id,
				allowedSwapPairs: allowedPairs,
			};

			await dispatch(updateCurrencySwapPairs({ ...jobData }));
			setVisible(false);
			form.resetFields();
			setRefresh(Math.random());
		} catch (error) {
			message.error('Failed to create money book');
			setIsLoadingPair(false);
		} finally {
			setIsLoadingPair(false);
		}
	};

	return (
		<div>
			<Button onClick={() => setVisible(true)}>Edit Swap Pairs</Button>

			<Modal
				visible={visible}
				onCancel={() => setVisible(false)}
				title="Edit Currency Swap"
				footer={null}
			>
				<div>
					<h2>Currency Swap Pairs</h2>
					<ul style={{ display: 'flex', gap: '6px' }}>
						{allowedPairs?.length > 0 ? (
							allowedPairs?.map((pair) => (
								<li
									key={pair.id}
									className="flex justify-between items-center py-1"
								>
									<span>{pair.symbol}</span>
									<button
										type="button"
										onClick={() => handleRemovePair(pair.id)}
										className="text-red-600 hover:underline"
									>
										x
									</button>
								</li>
							))
						) : (
							<p>No allowed swap pairs available</p>
						)}
					</ul>

					<Divider />

					<Form form={form} onFinish={handleSwapPairsUpdate}>
						<StyledFormItem
							name="currency"
							label="Select currency"
							rules={[
								{
									required: allowedPairs?.length === 0,
									message: 'Please select a currency!',
								},
							]}
						>
							<Select
								placeholder="Select a currency"
								options={currencySelect}
								onChange={(selectedOption) => handleAddPair(selectedOption)}
							/>
						</StyledFormItem>

						<Form.Item>
							<Button type="primary" htmlType="submit" loading={isLoadingPair}>
								{isLoadingPair ? 'Updating...' : 'Update Swap Pairs'}
							</Button>
							<Button
								onClick={() => setVisible(false)}
								style={{ marginLeft: '8px' }}
							>
								Cancel
							</Button>
						</Form.Item>
					</Form>
				</div>
			</Modal>
		</div>
	);
};

export default EditCurrencySwap;
